import React from 'react';

import { MergeFieldRegistryEntry } from '~forms/types';

import MergeFieldToken, { parseInlineTokens } from '../MergeFieldToken';
import { RICH_TEXT_ALLOWED_TAGS, RICH_TEXT_IMAGE_REF_ATTR, sanitizeRichTextHtml } from './sanitizeRichText';

// Second guard on the element-creation step, built from the sanitiser's own list so the two can
// never drift. Anything outside it cannot survive sanitise.
const ALLOWED_TAGS = new Set<string>(RICH_TEXT_ALLOWED_TAGS);

// Whitespace between table-structural tags has no valid place in the DOM: React warns and the
// browser hoists it out of the table. Tiptap does not emit it, but externally pasted markup can.
const TABLE_STRUCTURE_TAGS = new Set(['table', 'thead', 'tbody', 'tfoot', 'tr']);

/** Reference uuid -> resolved blob object URL, built by RichTextView's async resolution effect. */
export type FormImageMap = Record<string, string>;

// Split a text run on {{merge:<token>}} sentinels, rendering the tokens as the shared blue chip so
// rich-text merge fields look identical to inline merge fields used elsewhere (labels, prefills).
function renderTextWithTokens(text: string, registry: MergeFieldRegistryEntry[], keyPrefix: string): React.ReactNode {
  return parseInlineTokens(text).map((seg, i) =>
    seg.type === 'token' ? (
      <MergeFieldToken key={`${keyPrefix}-t${i}`} token={seg.value} registry={registry} />
    ) : (
      <React.Fragment key={`${keyPrefix}-s${i}`}>{seg.value}</React.Fragment>
    ),
  );
}

function nodeToReact(node: Node, registry: MergeFieldRegistryEntry[], images: FormImageMap, key: string): React.ReactNode {
  if (node.nodeType === Node.TEXT_NODE) {
    return <React.Fragment key={key}>{renderTextWithTokens(node.textContent ?? '', registry, key)}</React.Fragment>;
  }
  if (node.nodeType === Node.ELEMENT_NODE) {
    const el = node as Element;
    const tag = el.tagName.toLowerCase();
    // Defensive: a non-allow-listed element (shouldn't survive sanitise) renders as its children only.
    if (!ALLOWED_TAGS.has(tag)) {
      return <React.Fragment key={key}>{childrenToReact(el, registry, images, key)}</React.Fragment>;
    }
    if (tag === 'br') {
      return React.createElement('br', { key });
    }
    if (tag === 'img') {
      const ref = el.getAttribute(RICH_TEXT_IMAGE_REF_ATTR) ?? '';
      const objectUrl = images[ref];
      const width = el.getAttribute('width');
      const alt = el.getAttribute('alt') ?? '';
      if (!objectUrl) {
        // Not resolved yet, or the fetch failed. A sized placeholder box keeps surrounding text from
        // reflowing when the image arrives, and emits no src, so the browser issues no request of its
        // own. Both states render identically on purpose: a permanently missing image looks like an
        // image that has not arrived, not like a broken layout.
        return React.createElement('span', {
          key,
          className: 'rich-text-image-placeholder',
          role: 'img',
          'aria-label': alt || 'Image',
          style: width ? { width: `${width}px` } : undefined,
        });
      }
      return React.createElement('img', { key, src: objectUrl, alt, ...(width ? { width } : {}) });
    }
    return React.createElement(tag, { key }, childrenToReact(el, registry, images, key));
  }
  return null;
}

function childrenToReact(
  parent: Node,
  registry: MergeFieldRegistryEntry[],
  images: FormImageMap,
  keyPrefix: string,
): React.ReactNode[] {
  const parentTag = parent.nodeType === Node.ELEMENT_NODE ? (parent as Element).tagName.toLowerCase() : '';
  const isBlankTableWhitespace = (child: Node) =>
    TABLE_STRUCTURE_TAGS.has(parentTag) && child.nodeType === Node.TEXT_NODE && (child.textContent ?? '').trim() === '';
  const children = Array.from(parent.childNodes).filter((child) => !isBlankTableWhitespace(child));
  return children.map((child, i) => nodeToReact(child, registry, images, `${keyPrefix}-${i}`));
}

/**
 * Renders sanitised rich-text HTML as React, turning inline {{merge:<token>}} sentinels into the shared
 * MergeFieldToken chip. Used for the read-only filler view and the read-only canvas preview.
 *
 * Safer than dangerouslySetInnerHTML: only allow-listed tags are recreated and all text becomes escaped
 * React children. The DOMParser only ever sees already-sanitised markup.
 *
 * `images` maps a resolved `data-form-image` reference to its blob object URL. This function stays
 * synchronous: all async resolution lives in RichTextView's effect, and a missing map entry just
 * renders the placeholder branch above.
 */
export function renderRichText(
  html: string,
  registry: MergeFieldRegistryEntry[] = [],
  images: FormImageMap = {},
): React.ReactNode {
  const clean = sanitizeRichTextHtml(html);
  const doc = new DOMParser().parseFromString(clean, 'text/html');
  return childrenToReact(doc.body, registry, images, 'rt');
}
